import { ReactElement, useMemo } from 'react';
import { format } from 'date-fns';
import { useTheme } from '@mui/styles';

import { COLUMN_CELL_TYPES } from 'features/queue/grid/constants';
import { SelectRender } from 'features/queue/grid/defaultCellRender/SelectRender';
import Tooltip from 'common/components/tooltip';
import { AssigneeRender } from 'features/queue/grid/defaultCellRender/AssigneeRender';
import { CellComponentWrapper } from './style';

type CellComponentProps = {
  data: any;
  column: {
    colDef: {
      fieldTypeName: string;
      field: string;
      fieldId: string;
      customColumn: any;
      headerName: string;
      keyString: string;
      captionString: string;
      sortBy: string;
    };
  };
  rowIndex: number;
};

const convertTime = (timestamp: number) => format(new Date(timestamp), 'MM/dd/yyyy hh:mm:ss a');

const CellComponent = ({ data, column, rowIndex }: CellComponentProps): ReactElement => {
  const theme = useTheme();
  const srId = useMemo(() => (data?.length && data?.find((fields) => fields.field === 'id')?.value) || null, [data]);
  const srType = useMemo(
    () => (data?.length > 0 ? data?.find((fields) => fields.field === 'srType')?.value : undefined),
    [data],
  );
  const fieldTypeName = useMemo(() => column.colDef.fieldTypeName, [column.colDef.fieldTypeName]);
  const activeField = useMemo(
    () => (data?.length && data?.find((fields) => fields.field === column.colDef.field)) || null,
    [data, column],
  );

  const selectRenderStyle = useMemo(
    () => ({
      // @ts-ignore
      fontSize: theme?.fontSize?.main,
      // @ts-ignore
      color: theme?.palette?.text?.secondary_txt,
      minWidth: 90,
      minHeight: 24,
    }),
    [theme],
  );

  const getCellComponent = (fieldType) => {
    switch (fieldType) {
      case COLUMN_CELL_TYPES.NUMBER:
        return <span data-testId={`${srId}-id-cell`}>{srId}</span>;
      case COLUMN_CELL_TYPES.DATEANDTIME:
        return <Tooltip isTruncatedText text={convertTime(activeField?.value)} />;
      case COLUMN_CELL_TYPES.SELECT:
        if (column.colDef.field === 'assignee') {
          return (
            <AssigneeRender
              // @ts-ignore
              srId={srId}
              delayedHover={500}
              disabled
              initialAssigned={activeField?.value}
              rowIndex={rowIndex}
            />
          );
        }
        return (
          <SelectRender
            // @ts-ignore
            srId={srId}
            srType={srType}
            delayedHover={500}
            disabled
            fieldId={column?.colDef?.fieldId}
            customColumn={column?.colDef?.customColumn}
            fieldName={column?.colDef?.field}
            headerName={column.colDef.headerName}
            keyString={column.colDef.keyString}
            captionString={column.colDef.captionString}
            isCustomStyles
            isMultiple={fieldTypeName === COLUMN_CELL_TYPES.MULTISELECT}
            value={activeField?.value}
            style={selectRenderStyle}
            sortBy={column.colDef.sortBy}
            rowIndex={rowIndex}
          />
        );
      case COLUMN_CELL_TYPES.LONGTEXT:
      case COLUMN_CELL_TYPES.TEXT: {
        return <Tooltip isTruncatedText data-testId={`${srId}-text-cell`} text={activeField?.value} />;
      }
      default:
        return <></>;
    }
  };

  return <CellComponentWrapper data-field-type={fieldTypeName}>{getCellComponent(fieldTypeName)}</CellComponentWrapper>;
};

export default CellComponent;
